import { useState } from 'react';
import { ListChecks, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import type { VisioSeries, VisioSeriesInput } from '@jeraisonne/db/visio';
import { newSeriesPointId } from '@jeraisonne/db/visio';
import { useAuth } from '@/lib/auth';
import { TutorBookingTabs } from '@/components/tutor-booking-tabs';
import { VisioSeriesForm } from '@/components/visio-series-form';
import {
  useDeleteVisioSeries,
  useMyVisioSeries,
  useSaveVisioSeries,
  useSeedDefaultVisioSeries,
} from '@/lib/tutor/visio-series';

type Editing = { id?: string; initial: VisioSeriesInput } | null;

function toInput(series: VisioSeries): VisioSeriesInput {
  return { title: series.title, points: series.points, formatMd: series.formatMd };
}

/**
 * Séries de points à aborder du tuteur : il crée, modifie, supprime SES séries.
 * Pendant la visio il en choisit une pour cocher les points ; à la génération du
 * résumé, il choisit celle qui sert de trame (modifiable à chaque génération).
 */
export function TutorVisioSeriesPage() {
  const { user } = useAuth();
  const { data: series = [], isLoading } = useMyVisioSeries(user?.id);
  const save = useSaveVisioSeries(user?.id);
  const remove = useDeleteVisioSeries(user?.id);
  const seed = useSeedDefaultVisioSeries(user?.id);
  const [editing, setEditing] = useState<Editing>(null);

  function handleDelete(item: VisioSeries) {
    if (
      window.confirm(
        `Supprimer la série « ${item.title} » ? Les résumés déjà générés sont conservés.`,
      )
    ) {
      remove.mutate(item.id);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Rendez-vous
      </h1>
      <TutorBookingTabs />

      <p className="text-ink-500 mb-4 text-sm">
        Une série est une liste de points à aborder. Pendant la visio tu la coches au fil de
        l’échange ; à la fin, le résumé répond à chaque point à partir du dialogue.
      </p>

      {editing ? (
        <VisioSeriesForm
          key={editing.id ?? 'new'}
          initial={editing.initial}
          saving={save.isPending}
          error={save.isError ? save.error.message : null}
          onCancel={() => {
            save.reset();
            setEditing(null);
          }}
          onSubmit={(input) =>
            save.mutate(
              { id: editing.id, series: input },
              {
                onSuccess: () => {
                  save.reset();
                  setEditing(null);
                },
              },
            )
          }
        />
      ) : (
        <div className="space-y-3">
          <Button
            onClick={() =>
              setEditing({
                initial: {
                  title: '',
                  points: [{ id: newSeriesPointId(), text: '' }],
                  formatMd: null,
                },
              })
            }
            className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
          >
            <Plus className="size-4" />
            Nouvelle série
          </Button>

          {isLoading && (
            <div className="flex justify-center py-10">
              <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          )}

          {!isLoading && series.length === 0 && (
            <div className="border-ink-100 rounded-2xl border bg-white p-5 text-center">
              <ListChecks className="text-ink-300 mx-auto h-8 w-8" />
              <p className="text-ink-700 mt-2 text-sm font-semibold">Aucune série pour l’instant</p>
              <p className="text-ink-500 mt-1 text-xs">
                Pars de nos modèles (cours, session d’étude, appel rapide, entretien de suivi) ou
                crée la tienne.
              </p>
              <Button
                variant="outline"
                onClick={() => seed.mutate()}
                loading={seed.isPending}
                className="mt-3 min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
              >
                Ajouter les séries de départ
              </Button>
              {seed.isError && (
                <p role="alert" className="text-accent-700 mt-2 text-xs font-medium">
                  {seed.error.message}
                </p>
              )}
            </div>
          )}

          {series.map((item) => (
            <div key={item.id} className="border-ink-100 rounded-2xl border bg-white p-4">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 text-sm font-semibold">{item.title}</p>
                  <p className="text-ink-400 text-xs">
                    {item.points.length} point{item.points.length > 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing({ id: item.id, initial: toInput(item) })}
                  aria-label={`Modifier la série ${item.title}`}
                  className="text-ink-500 flex size-11 shrink-0 items-center justify-center rounded-lg active:scale-90"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  aria-label={`Supprimer la série ${item.title}`}
                  className="text-ink-500 hover:text-accent-700 flex size-11 shrink-0 items-center justify-center rounded-lg active:scale-90"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
              <ol className="text-ink-600 mt-2 list-inside list-decimal space-y-0.5 text-xs">
                {item.points.slice(0, 3).map((point) => (
                  <li key={point.id} className="truncate">
                    {point.text}
                  </li>
                ))}
                {item.points.length > 3 && (
                  <li className="text-ink-400 list-none">
                    … et {item.points.length - 3} autre{item.points.length - 3 > 1 ? 's' : ''}
                  </li>
                )}
              </ol>
            </div>
          ))}

          {remove.isError && (
            <p role="alert" className="text-accent-700 text-xs font-medium">
              {remove.error.message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
